import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { EmailAuthPage } from './EmailAuthPage';

type AuthPolicy = {
  user?: { id: string } | null;
  emailAuthEnabled?: boolean;
  testLoginEnabled?: boolean;
  loginStrategy?: 'email' | 'test' | 'unavailable';
};

export function AuthRouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isLogin = location.pathname === '/login';
  const isVerify = location.pathname === '/verify-email';
  const isReset = location.pathname === '/reset-password';
  const isPublicAuthRoute = isLogin || isVerify || isReset;
  const [policy, setPolicy] = useState<AuthPolicy | null>(null);
  const [message, setMessage] = useState('正在进入安全登录…');

  useEffect(() => {
    let cancelled = false;
    if (!isPublicAuthRoute) {
      setPolicy(null);
      return () => { cancelled = true; };
    }
    if (isLogin && new URLSearchParams(location.search).get('test') === '1') {
      window.location.replace('/auth/test-login');
      return () => { cancelled = true; };
    }
    fetch('/api/auth/session', { credentials: 'same-origin', cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Auth policy request failed: ${response.status}`);
        return response.json() as Promise<AuthPolicy>;
      })
      .then((value) => {
        if (cancelled) return;
        if (isLogin && value.user) {
          window.location.replace('/app');
          return;
        }
        if (value.loginStrategy === 'test' && isLogin) {
          window.location.replace('/auth/test-login');
          return;
        }
        if (!value.emailAuthEnabled) {
          setMessage('当前环境没有启用邮箱登录。');
          setPolicy({ ...value, loginStrategy: 'unavailable' });
          return;
        }
        setPolicy(value);
      })
      .catch(() => {
        if (!cancelled) {
          setMessage('无法读取登录配置，请刷新后重试。');
          setPolicy({ loginStrategy: 'unavailable' });
        }
      });
    return () => { cancelled = true; };
  }, [isLogin, isPublicAuthRoute, location.search]);

  if (!isPublicAuthRoute) return children;
  if (!policy) return <AuthStatus message={message} />;
  if (policy.loginStrategy === 'unavailable') return <AuthStatus message={message} />;
  return <EmailAuthPage kind={isVerify ? 'verify' : isReset ? 'reset' : 'login'} />;
}

function AuthStatus({ message }: { message: string }) {
  return <main className="auth-page" aria-live="polite"><section className="auth-card"><h1>FeedDive</h1><p>{message}</p></section></main>;
}
